import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, FileText, Loader2, Send } from "lucide-react";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { ProfileView } from "@/components/company/ProfileView";
import { useDemo } from "@/lib/demo/store";
import { useI18n } from "@/lib/i18n/i18n";
import { directoryCompanies } from "@/lib/demo/companies";
import { fullName, DAY_LABELS } from "@/lib/demo/utils";

const ApplyCard = ({ company }) => {
  const { state, activeParticipant: p, recordCompanyScan } = useDemo();
  const { t } = useI18n();
  const [sending, setSending] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const existing = state.interactions.find((i) => i.companyId === company.id && i.participantId === p.id);

  const send = () => {
    setSending(true);
    setTimeout(() => {
      recordCompanyScan(company.id, p.token, null, "WEB");
      setSending(false);
      setJustSent(true);
    }, 800);
  };

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm lg:sticky lg:top-24" data-testid="company-apply-card">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-upv">{t("Comparte tu currículum")}</p>
      <div className="mt-4 flex items-center gap-3 rounded-2xl bg-slate-50 p-4">
        <FileText className="h-5 w-5 shrink-0 text-upv" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{fullName(p)}</p>
          <p className="truncate font-mono text-xs text-slate-500">{p.cvFileName}</p>
        </div>
      </div>
      {existing ? (
        <div className={`mt-5 rounded-2xl p-5 ${justSent ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-800"}`} data-testid="company-apply-done">
          <p className="flex items-center gap-2 font-display text-lg font-bold"><CheckCircle2 className="h-5 w-5" /> {justSent ? t("Currículum enviado") : t("Ya compartiste tu currículum con {company}", { company: company.name })}</p>
          <p className={`mt-1 text-sm ${justSent ? "text-white/80" : "text-emerald-700"}`}>
            {t(DAY_LABELS[existing.day])} · {existing.time} · {existing.method === "WEB" ? t("Web · perfil de empresa") : t("Código QR")}
          </p>
        </div>
      ) : (
        <>
          <p className="mt-4 text-sm text-slate-600">{t("Se compartirá tu perfil profesional y currículum con {company}.", { company: company.name })}</p>
          <button onClick={send} disabled={sending} className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-upv font-display font-bold tracking-wider text-white transition-colors hover:bg-upv-dark disabled:opacity-80" data-testid="company-apply-button">
            {sending ? <><Loader2 className="h-5 w-5 animate-spin" /> {t("Enviando…")}</> : <><Send className="h-4 w-4" /> {t("ENVIAR MI CURRÍCULUM")}</>}
          </button>
        </>
      )}
      <p className="mt-4 text-xs text-slate-400">{t("También puedes mostrar tu QR en el stand {stand}.", { stand: company.stand })}</p>
    </div>
  );
};

export default function EmpresaPerfil() {
  const { id } = useParams();
  const { t } = useI18n();
  const { state } = useDemo();
  const company = directoryCompanies(state).find((c) => c.id === id);
  if (!company) return <Navigate to="/empresas" replace />;

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="company-profile-page">
      <SiteHeader />
      <ProfileView
        company={company}
        aside={<ApplyCard company={company} />}
        topLeft={<Link to="/empresas" className="absolute left-5 top-5 flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-xs font-semibold backdrop-blur transition-colors hover:bg-white lg:left-10" data-testid="company-back-link"><ArrowLeft className="h-3.5 w-3.5" /> {t("Todas las empresas")}</Link>}
      />
      <PageFooter />
    </div>
  );
}
